'use client';

import { c as _c } from "react/compiler-runtime";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type PropsWithChildren, useEffect } from 'react';
import { useGlobalCmsContentContext } from '@module/mdrt-org/shared/components/providers/global-cms-context';
import { type FetchError, InternalServerError, NotAuthenticated, NotAuthorized } from '@module/mdrt-org/shared/utils/http-services/error-definitions';
import { ToastType } from '@shared/ui-components/toast/constants/constants';
import { GlobalToastContextProvider, useGlobalToastContext } from '@shared/ui-components/toast/context/global-toast-context';
import { GlobalLoaderContextProvider } from './global-loader-context';
import { GTMClickTracker } from './gtm-click-tracker';
import { SubNavigationContextProvider } from './sub-navigation-context';
const errorToastRef: {
  current: ((error: FetchError) => void) | null;
} = {
  current: null
};
const makeQueryClient = () => new QueryClient({
  defaultOptions: {
    mutations: {
      throwOnError: false
    },
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
      throwOnError: false
    }
  },
  mutationCache: new MutationCache({
    onError: (error: Error) => {
      if (error instanceof NotAuthenticated || error instanceof NotAuthorized || error instanceof InternalServerError) {
        errorToastRef.current?.(error);
      }
    }
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      if ((error instanceof NotAuthenticated || error instanceof NotAuthorized || error instanceof InternalServerError) && query.meta?.disableToastOnCriticalError !== true) {
        errorToastRef.current?.(error);
      }
    }
  })
});
let browserQueryClient: QueryClient | undefined;
const getQueryClient = () => {
  if (typeof window === 'undefined') {
    return makeQueryClient();
  } else {
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
};
const AppContextProvider = t0 => {
  const $ = _c(9);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getQueryClient();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const queryClient = t1;
  const {
    cmsContent
  } = useGlobalCmsContentContext();
  const {
    addToast
  } = useGlobalToastContext();
  let t2;
  let t3;
  if ($[1] !== addToast || $[2] !== cmsContent.closeButtonLabel || $[3] !== cmsContent.somethingWentWrong) {
    t2 = () => {
      errorToastRef.current = () => addToast({
        closeButtonLabel: cmsContent.closeButtonLabel,
        description: cmsContent.somethingWentWrong,
        type: ToastType.ERROR
      }, {
        timeout: 0
      });
    };
    t3 = [addToast, cmsContent.closeButtonLabel, cmsContent.somethingWentWrong];
    $[1] = addToast;
    $[2] = cmsContent.closeButtonLabel;
    $[3] = cmsContent.somethingWentWrong;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ReactQueryDevtools initialIsOpen={false} />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children) {
    t5 = <QueryClientProvider client={queryClient}>{t4}<SubNavigationContextProvider><GTMClickTracker>{children}</GTMClickTracker></SubNavigationContextProvider></QueryClientProvider>;
    $[7] = children;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export const Providers = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <GlobalToastContextProvider><GlobalLoaderContextProvider><AppContextProvider>{children}</AppContextProvider></GlobalLoaderContextProvider></GlobalToastContextProvider>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};